import React, { useEffect, useMemo, useState } from "react";
import {
    GridApiPro,
    GridColDef,
    GridPaginationModel,
} from "@mui/x-data-grid-pro";
import { useLazyQuery } from "@apollo/client";
import _ from "lodash";
import { SearchSystems } from "../../graphql/queries/searchSystems";
import { paginationLimit } from "../../config/paginationLimit";
import { buildDynamicListFilter } from "../../utils/buildDynamicListFilter";
import { TypeDefinitionAttribute } from "../../types";
import { Typography } from "@mui/joy";
import { useTranslation } from "react-i18next";
import { connectionStatusChipFactory } from "../../utils/overallStatusIconFactory";
import { getSystemTypeLabel } from "../../config/systemTypes";
import { getInitialPagination } from "@dreebit-registry/vsm-microservice-components";
import IotSystemRegistrationModal from "../InstalledBaseTreeColumn/iotSystemRegistrationModal";
import Table from "../Table";
import { useTableColumnConfig } from "../../hooks/useTableColumnConfig";
import { getParentPathNode } from "../../utils/installedBasePath";
import InstalledBaseNodeParentCell from "../InstalledBaseNodeParentCell";

interface Props {
    height: number;
    onChangePagination?: (newPaginationModel: GridPaginationModel) => void;
    urlParams?: URLSearchParams;
    onOpenSystem?: (systemId: string) => void;
    searchParams?: { query: string; fields: string[] };
    filter?: Record<string, any>;
    attributes?: TypeDefinitionAttribute[];
    apiRef?: React.MutableRefObject<GridApiPro>;
    panelAnchorRef?: React.RefObject<HTMLElement | null>;
}

const DEFAULT_VISIBILITY = { location: false };

const SystemsTable = ({
    height,
    onChangePagination,
    urlParams,
    onOpenSystem,
    searchParams,
    filter,
    attributes,
    apiRef,
    panelAnchorRef,
}: Props) => {
    const { t } = useTranslation();

    const [paginationModel, setPaginationModel] = useState(
        getInitialPagination(paginationLimit, urlParams)
    );
    const [iotModalOpen, setIotModalOpen] = useState(false);
    const [selectedSystem, setSelectedSystem] = useState<{ id: string; title: string } | null>(null);

    const [fetchSystemList, systemListQuery] = useLazyQuery(SearchSystems, {
        fetchPolicy: "network-only",
    });

    const columns = useMemo<GridColDef[]>(() => [
        {
            field: "title",
            headerName: t("Title"),
            flex: 1,
            valueGetter: (value, row) => {
                return _.get(row, "system.title");
            },
            renderCell: (params) => {
                const title = _.get(params.row, "system.title");
                const systemId = _.get(params.row, "system.id");

                return (
                    <Typography
                        fontWeight={"bold"}
                        sx={{ cursor: "pointer" }}
                        color={"primary"}
                        onClick={() => onOpenSystem?.(systemId)}
                    >
                        {title}
                    </Typography>
                );
            },
            display: "flex",
            hideable: false,
        },
        {
            field: "systemType",
            headerName: t("System Type"),
            flex: 1,
            valueGetter: (value, row) => {
                return getSystemTypeLabel(_.get(row, "system.systemType"), t);
            },
        },
        {
            field: "imei",
            headerName: t("IMEI"),
            flex: 1,
            valueGetter: (value, row) => {
                return _.get(row, "system.iotConnection.gateway.imei");
            },
        },
        {
            field: "client",
            headerName: t("Company"),
            flex: 1,
            valueGetter: (value, row) => {
                return _.get(row, "system.client.name");
            },
        },
        {
            field: "location",
            headerName: t("Location"),
            flex: 1,
            display: "flex",
            valueGetter: (value, row) =>
                getParentPathNode(_.get(row, "system.installedBaseNode"))?.title ?? "",
            renderCell: (params) => (
                <InstalledBaseNodeParentCell
                    installedBaseNode={_.get(params.row, "system.installedBaseNode")}
                />
            ),
        },
        {
            field: "assetCount",
            headerName: t("Asset Count"),
            width: 120,
            valueGetter: (value, row) => {
                return _.get(row, "system.assetList.total");
            },
        },
        {
            field: "connectionStatus",
            headerName: t("IoT System Status"),
            width: 200,
            display: "flex",
            align: "center",
            renderCell: (params) => {
                const connectionStatus = _.get(params.row, "system.iotConnection.connectionStatus", undefined);
                return connectionStatusChipFactory(connectionStatus, t, { isSystem: true });
            },
        },
    ], [t, onOpenSystem]);

    const columnConfig = useTableColumnConfig({
        storageKey: 'systemList_columnConfig',
        columns,
        defaultVisibility: DEFAULT_VISIBILITY,
    });

    const systemsListData = _.get(
        systemListQuery,
        "data.searchSystems.searchSystemResults",
        []
    );
    const totalSystems = _.get(systemListQuery, "data.searchSystems.total");

    const filterAttrs = (attributes ?? []).filter(a => a.index !== "search");

    useEffect(() => {
        fetchSystemList({
            variables: {
                pagination: {
                    start: paginationModel.page * paginationModel.pageSize,
                    limit: paginationModel.pageSize,
                },
                query: searchParams?.query ? {
                    query: searchParams.query,
                    fields: searchParams.fields
                } : undefined,
                filter: buildDynamicListFilter(filter ?? {}, filterAttrs),
            },
        });
    }, [fetchSystemList, paginationModel, searchParams, filter, attributes]);

    useEffect(() => {
        if (onChangePagination) {
            onChangePagination(paginationModel);
        }
    }, [paginationModel]);

    return (
        <>
            <div style={{ width: "100%", height: height }}>
                <Table
                    loading={systemListQuery.loading}
                    data={systemsListData}
                    getRowId={(res: any) => res.system.id}
                    rowCount={totalSystems}
                    {...columnConfig.gridProps}
                    pagination={true}
                    paginationMode="server"
                    paginationModel={paginationModel}
                    pageSizeOptions={[paginationLimit]}
                    onPaginationModelChange={setPaginationModel}
                    apiRef={apiRef}
                    panelAnchorRef={panelAnchorRef}
                    disableColumnFilter
                    disableColumnSorting
                    disableColumnMenu
                    disableRowSelectionOnClick
                />
            </div>
            {iotModalOpen && selectedSystem && (
                <IotSystemRegistrationModal
                    open={iotModalOpen}
                    onClose={() => {
                        setIotModalOpen(false);
                        setSelectedSystem(null);
                    }}
                    node={selectedSystem!}
                    refetchFunction={() => fetchSystemList({
                        variables: {
                            pagination: {
                                start: paginationModel.page * paginationModel.pageSize,
                                limit: paginationModel.pageSize,
                            },
                            query: searchParams?.query ? {
                                query: searchParams.query,
                                fields: searchParams.fields
                            } : undefined,
                            filter: buildDynamicListFilter(filter ?? {}, filterAttrs),
                        },
                    })}
                />
            )}
        </>
    );
};

export default SystemsTable;
